import React from 'react';
import Card from '@/components/ui/Card';
import { Workspace } from '@/types';
import RoleBadge from './RoleBadge';
import UserActionsMenu from './UserActionsMenu';
import { UserItem } from './types';

const UserTable: React.FC<{
  users: UserItem[];
  totalCount: number;
  editingId: string | null;
  resetPasswordId: string | null;
  workspaces: Workspace[];
  currentUserId?: string;
  selectedIds: Set<string>;
  allSelected: boolean;
  someSelected: boolean;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  hasMore: boolean;
  sentinelRef: (node: Element | null) => void;
  onImpersonate: (user: UserItem) => void;
  onEdit: (user: UserItem) => void;
  onResetPassword: (user: UserItem) => void;
  onDelete: (user: UserItem) => void;
  renderEditForm: () => React.ReactNode;
  renderResetPasswordForm: () => React.ReactNode;
}> = ({
  users,
  totalCount,
  editingId,
  resetPasswordId,
  workspaces,
  currentUserId,
  selectedIds,
  allSelected,
  someSelected,
  onToggleSelect,
  onToggleSelectAll,
  hasMore,
  sentinelRef,
  onImpersonate,
  onEdit,
  onResetPassword,
  onDelete,
  renderEditForm,
  renderResetPasswordForm,
}) => {
  const th = 'text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';
  return (
    <Card padding="none">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="px-6 py-3 w-10">
                <input
                  type="checkbox"
                  aria-label="Select all users"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected; }}
                  onChange={onToggleSelectAll}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className={th}>Name</th>
              <th className={th}>Email</th>
              <th className={th}>Role</th>
              <th className={th}>Created</th>
              <th className={th}>Workspaces</th>
              <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {users.map((user) => (
              <React.Fragment key={user.id}>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      aria-label={`Select ${user.name}`}
                      checked={selectedIds.has(user.id)}
                      disabled={user.id === currentUserId}
                      onChange={() => onToggleSelect(user.id)}
                      className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                    />
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 font-medium">{user.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{user.email}</td>
                  <td className="px-6 py-4"><RoleBadge role={user.role} /></td>
                  <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {user.workspaceIds && user.workspaceIds.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {user.workspaceIds.map((wid) => (
                          <span key={wid} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">
                            {workspaces.find((w) => w.id === wid)?.shortName || '…'}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">— none —</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end">
                      <UserActionsMenu
                        user={user}
                        currentUserId={currentUserId}
                        onImpersonate={onImpersonate}
                        onEdit={onEdit}
                        onResetPassword={onResetPassword}
                        onDelete={onDelete}
                      />
                    </div>
                  </td>
                </tr>

                {editingId === user.id && (
                  <tr><td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50">{renderEditForm()}</td></tr>
                )}
                {resetPasswordId === user.id && (
                  <tr><td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50">{renderResetPasswordForm()}</td></tr>
                )}
              </React.Fragment>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                No users found. Click "Add User" to create one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {hasMore && <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
      {users.length > 0 && (
        <div className="px-6 py-3 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
          Showing {users.length} of {totalCount}
        </div>
      )}
    </Card>
  );
};

export default UserTable;
